import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  useFonts,
} from '@expo-google-fonts/inter';
import {
  browserProfilingIntegration,
  browserTracingIntegration,
  httpClientIntegration,
  init as initSentry,
  replayIntegration,
} from '@sentry/browser';
import { ThemeProvider } from '@shopify/restyle';
import React from 'react';
import { Helmet } from 'react-helmet';
import { View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import './src/i18n/i18n.config.ts';

import { appInfo } from './app.config';
import { GraphQLErrorMonitor } from './src/components/shared/GraphQLErrorMonitor';
import { SENTRY_FAILED_REQUEST_STATUS_CODES } from './src/constants/sentryHttpClient';
import { AuthProvider } from './src/context/authContext';
import { MixpanelProvider } from './src/context/MixpanelContext';
import { NetInfoProvider } from './src/context/NetInfoContext';
import { ApolloClientProvider } from './src/hooks/useApolloClient';
import { AppNavigator } from './src/navigation/app-navigator';
import { loadGtm } from './src/utils/loadGtm.web';
import { shouldIgnoreSentryError } from './src/utils/sentry/errorFilter';
import theme from './themes/theme';

if (process.env.NODE_ENV !== 'test') {
  initSentry({
    dsn: process.env.SENTRY_DSN,
    debug: false,
    environment: process.env.SENTRY_ENV,
    release: appInfo.version,
    enabled: !__DEV__,
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,
    tracePropagationTargets: [
      'localhost',
      ...(process.env.GRAPHQL_ENDPOINT ? [process.env.GRAPHQL_ENDPOINT] : []),
    ],
    attachStacktrace: true,
    beforeSend(event) {
      const sentryEvent = event as Parameters<
        typeof shouldIgnoreSentryError
      >[0];
      if (shouldIgnoreSentryError(sentryEvent)) {
        return null;
      }
      if (
        event.exception?.values?.some((e) =>
          e.value?.includes('messaging/unsupported-browser')
        )
      ) {
        return null;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb, hint) {
      // Add operation name for GraphQL operations
      if (breadcrumb.category === 'fetch' && hint?.input?.[1]?.body) {
        try {
          const body = JSON.parse(hint.input?.[1]?.body);
          if (body.operationName) {
            return {
              ...breadcrumb,
              data: {
                ...breadcrumb.data,
                operationName: body.operationName,
              },
            };
          }
        } catch {
          // Ignore parsing errors
        }
      }
      return breadcrumb;
    },
    beforeSendTransaction(event) {
      const spanIdToOperationName = new Map<string, unknown>();
      for (const breadcrumb of event.breadcrumbs ?? []) {
        const spanId = breadcrumb.data?.__span;
        const operationName = breadcrumb.data?.operationName;
        if (spanId && operationName) {
          spanIdToOperationName.set(spanId, operationName);
        }
      }
      const formattedSpans = event.spans?.map((span) => ({
        ...span,
        data: {
          ...span.data,
          operationName: spanIdToOperationName.get(span.span_id),
        },
      }));
      return { ...event, spans: formattedSpans };
    },
    integrations: [
      browserTracingIntegration({
        idleTimeout: 5000,
        shouldCreateSpanForRequest: (url) => {
          return !url.match(/\/health\/?$/);
        },
      }),
      browserProfilingIntegration(),
      httpClientIntegration({
        // 401 excluded from automatic capture: recoverable 401s (race-condition
        // guard succeeded) are logged as breadcrumbs; unrecoverable 401s that
        // force logout are captured explicitly via Sentry.captureMessage in errorLink.
        // 429 excluded (TA-10842): the backend's brute-force login throttle is
        // working as designed, and useApolloClient.tsx's errorLink now surfaces
        // it to the user directly — capturing it here would just be noise.
        // Shared with App.tsx so the two ranges stay in sync.
        failedRequestStatusCodes: SENTRY_FAILED_REQUEST_STATUS_CODES,
        failedRequestTargets: [/.*/],
      }),
      replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

if (process.env.NODE_ENV !== 'test') {
  loadGtm();
}

// Record the start time as early as possible
const startTime = typeof performance !== 'undefined' ? performance.now() : 0;

export default function App() {
  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
  });

  // app is loading
  if (!fontsLoaded) {
    return <View />;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const invitationToken = urlParams.get('invitation_token') ?? '';
  const type = urlParams.get('type') ?? '';
  const id = urlParams.get('id') ?? '';
  const requestId = urlParams.get('request_id') ?? '';

  if (invitationToken || type || id) {
    window.sessionStorage.setItem('invitation_token', invitationToken);
    window.sessionStorage.setItem('link_type', type);
    window.sessionStorage.setItem('task_or_project_id', id);
    window.sessionStorage.setItem('task_join_request_id', requestId);
  } else {
    const path = window.location.pathname;
    if (path.startsWith('/account_invitations/')) {
      const code = path.substring('/account_invitations/'.length);
      window.sessionStorage.setItem('invitation_token', code);
      window.sessionStorage.setItem('link_type', 'team');
    }
  }

  return (
    <>
      <Helmet>
        <meta name='apple-itunes-app' content='app-id=1635406641' />
        <meta
          name='description'
          content='Manage your tasks, projects, and teams more efficiently with TaskTag your all-in-one productivity and collaboration platform.'
        />
      </Helmet>
      <GestureHandlerRootView style={{ flex: 1 }}>
        <MixpanelProvider>
          <NetInfoProvider>
            <ApolloClientProvider>
              <GraphQLErrorMonitor>
                <AuthProvider>
                  <SafeAreaProvider>
                    <ThemeProvider theme={theme}>
                      <AppNavigator startTime={startTime} />
                      {/* Pass startTime */}
                    </ThemeProvider>
                  </SafeAreaProvider>
                </AuthProvider>
              </GraphQLErrorMonitor>
            </ApolloClientProvider>
          </NetInfoProvider>
        </MixpanelProvider>
      </GestureHandlerRootView>
    </>
  );
}
